import { Controller, useForm, useWatch } from "react-hook-form";
import { useCallback, useEffect } from "react";
import { useIsMutating } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button2 } from "@/ui2/atoms/Button";
import { CosignatureRequirementReason, UUID } from "@/core/types";
import { ModalControlled2, ModalDivider } from "@/ui2/molecules/Modal";
import { permissionsKeys } from "@/features/permissions/api/keyFactory";
import { Radio2 } from "@/ui2/atoms/Radio";
import { RadioGroup2 } from "@/ui2/molecules/RadioGroup";
import { TextField2 } from "@/ui2/molecules/TextField";

import { AssignedSupervisorsSelect } from "./components/AssignedSupervisorsSelect";
import { cosignatureFormSchema, CosignatureFormValues } from "./schema";

type RequireCosignatureModalProps = {
  initialValues?: Partial<CosignatureFormValues>;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CosignatureFormValues) => void;
  userId: UUID;
};

export function RequireCosignatureModal({
  initialValues,
  isOpen,
  onClose,
  onSubmit,
  userId,
}: RequireCosignatureModalProps) {
  /** The api returns a number representing the number of mutations in progress
   * !! operator converts the number to a boolean value
   */
  const isMutating = !!useIsMutating({
    mutationKey: permissionsKeys.updateUserPermissions(userId),
  });
  const { control, formState, handleSubmit, reset, setValue } = useForm<CosignatureFormValues>({
    defaultValues: { requirementReason: "", requirementReasonDetails: "", supervisorId: "" },
    mode: "onChange",
    resolver: zodResolver(cosignatureFormSchema),
  });

  useEffect(() => {
    if (isOpen) {
      reset({
        requirementReason: initialValues?.requirementReason ?? "",
        requirementReasonDetails: initialValues?.requirementReasonDetails ?? "",
        supervisorId: initialValues?.supervisorId ?? "",
      });
    }
  }, [isOpen, initialValues, reset]);

  const reasonWatch = useWatch({ control, name: "requirementReason" });
  const isOtherSelected = reasonWatch === "Other";

  useEffect(() => {
    if (!isOtherSelected) setValue("requirementReasonDetails", "");
  }, [isOtherSelected, setValue]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const onFormSubmit = handleSubmit((data) => {
    onSubmit(data);
  });

  return (
    <ModalControlled2 isOpen={isOpen} onOpenChange={handleClose} title="Co-Sign Required">
      <form className="tw:flex tw:flex-col tw:gap-4" onSubmit={onFormSubmit}>
        <p className="body-md">
          All clinical notes from this provider will need co-signature from an approved supervisor
          before being finalized.
        </p>

        <AssignedSupervisorsSelect control={control} />

        <Controller
          control={control}
          name="requirementReason"
          render={({ field }) => (
            <RadioGroup2 label="Reason" onChange={field.onChange} value={field.value}>
              {RADIO_OPTIONS.map((option) => (
                <Radio2 key={option.id} value={option.value}>
                  {option.name}
                </Radio2>
              ))}
            </RadioGroup2>
          )}
        />

        {isOtherSelected && (
          <Controller
            control={control}
            name="requirementReasonDetails"
            render={({ field }) => (
              <TextField2
                aria-label="Other Reason"
                mode="textarea"
                onChange={field.onChange}
                placeholder="Provide specific reasoning..."
                textAreaProps={{ className: "tw:min-h-19.5 tw:max-h-40" }}
                value={field.value}
              />
            )}
          />
        )}

        <ModalDivider />

        <div className="tw:flex tw:justify-end tw:gap-3">
          <Button2 onPress={handleClose} variant="secondary">
            Cancel
          </Button2>

          <Button2
            isDisabled={!formState.isValid || isMutating}
            isLoading={isMutating}
            labelLoading={null}
            type="submit"
          >
            Approve
          </Button2>
        </div>
      </form>
    </ModalControlled2>
  );
}

type Option = {
  id: Lowercase<CosignatureRequirementReason>;
  name: CosignatureRequirementReason;
  value: CosignatureRequirementReason;
};

const RADIO_OPTIONS = [
  { id: "lmsw", name: "LMSW", value: "LMSW" },
  { id: "other", name: "Other", value: "Other" },
] as const satisfies Option[];
